/**
 * "Report a bug" dialog opened from the signed-in user menu (2026-09-30).
 *
 * Contract: POST /bug-reports (see submitBugReport). Rules worth keeping:
 *  - A 429 (5 reports/hour/user) is shown once and NEVER retried automatically.
 *  - A clean form closes immediately; a dirty one asks "Discard report?" inline
 *    on the first close attempt (no window.confirm).
 *  - Two in-flight phases: `captcha` (bounded by CAPTCHA_TIMEOUT_MS, close allowed;
 *    closing cancels the send) and `sending` (the POST is out; close is ignored so
 *    a late response cannot land on a dialog the user believes is gone).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { submitBugReport } from '../api/guardianApi';
import { executeRecaptcha } from '../utils/recaptcha';
import {
  BUG_REPORT_LIMITS as L,
  buildBugReportBody,
  captureBugReportContext,
  classifyBugReportResult,
  countChars,
  emptyBugReportFields,
  isBugReportDirty,
  validateBugReport,
} from '../utils/bugReport';

const TITLE_ID = 'bug-report-dialog-title';
export const CAPTCHA_TIMEOUT_MS = 15000;

/** Rejects with `{ timedOut: true }` when `promise` has not settled within `ms`. */
function withTimeout(promise, ms) {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject({ timedOut: true }), ms); // eslint-disable-line prefer-promise-reject-errors
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {string|null} props.authToken - Guardian bearer token.
 * @param {object|null} [props.user] - Signed-in Guardian user; `email` prefills the contact field.
 *   Mount with a fresh `key` per open to reset the form.
 */
export default function BugReportDialog({ open, onClose, authToken, user = null }) {
  // Captured once per mount: the parent remounts this component (new `key`) on every
  // open, so each report starts fresh and a later profile refresh cannot wipe typing.
  const [prefillEmail] = useState(() => user?.email || '');
  const [fields, setFields] = useState(() => emptyBugReportFields(prefillEmail));
  const [touched, setTouched] = useState({});
  const [showAll, setShowAll] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const phase = useRef('idle'); // 'idle' | 'captcha' | 'sending'
  const cancelled = useRef(false); // set once the dialog is closed or unmounted
  const [sending, setSending] = useState(false); // POST out: close is blocked

  useEffect(() => {
    cancelled.current = false;
    return () => {
      cancelled.current = true;
    };
  }, []);

  const errors = validateBugReport(fields);
  const dirty = isBugReportDirty(fields, prefillEmail);
  const context = open ? captureBugReportContext() : { pageUrl: '', userAgent: '' };

  const setField = (name) => (e) => {
    const { value } = e.target;
    setFields((prev) => ({ ...prev, [name]: value }));
    setErrorMessage('');
  };
  const markTouched = (name) => () => setTouched((prev) => ({ ...prev, [name]: true }));
  const fieldError = (name) => ((showAll || touched[name]) && errors[name]) || '';

  /** Closes for good; a pending captcha wait then sends nothing. */
  const closeNow = useCallback(() => {
    cancelled.current = true;
    onClose();
  }, [onClose]);

  /** Esc / backdrop / X / Cancel all funnel here. */
  const requestClose = useCallback(() => {
    if (phase.current === 'sending') return;
    if (sent || !dirty) {
      closeNow();
      return;
    }
    setConfirmDiscard(true);
  }, [sent, dirty, closeNow]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (phase.current !== 'idle') return;
    if (Object.keys(errors).length > 0) {
      setShowAll(true);
      return;
    }
    phase.current = 'captcha';
    setSubmitting(true);
    setErrorMessage('');
    try {
      let recaptchaToken;
      try {
        // Passed through as-is: returns 'disabled' when captcha is off, like the auth forms.
        recaptchaToken = await withTimeout(executeRecaptcha('bug_report'), CAPTCHA_TIMEOUT_MS);
      } catch (err) {
        if (cancelled.current) return;
        if (err?.timedOut) {
          setErrorMessage('Verification timed out. Try again.');
        } else {
          console.warn('Bug report captcha failed:', err);
          setErrorMessage('Captcha check failed. Please try again.');
        }
        return;
      }
      // Closed while waiting on the captcha: send nothing.
      if (cancelled.current) return;
      phase.current = 'sending';
      setSending(true);
      const body = buildBugReportBody(fields, { recaptchaToken, ...captureBugReportContext() });
      const res = await submitBugReport(authToken, body);
      if (cancelled.current) return;
      const outcome = classifyBugReportResult(res);
      if (outcome.kind === 'sent') {
        setFields(emptyBugReportFields(prefillEmail));
        setTouched({});
        setShowAll(false);
        setSent(true);
        return;
      }
      if (outcome.kind === 'validation') setShowAll(true);
      setErrorMessage(outcome.message);
    } finally {
      phase.current = 'idle';
      setSending(false);
      setSubmitting(false);
    }
  };

  const multiline = { multiline: true, fullWidth: true, margin: 'dense', size: 'small' };

  return (
    <Dialog
      open={open}
      onClose={requestClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby={TITLE_ID}
    >
      <DialogTitle id={TITLE_ID} sx={{ pr: 6, fontWeight: 600 }}>
        Report a bug
        <IconButton
          aria-label="Close"
          onClick={requestClose}
          disabled={sending}
          sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {sent ? (
        <>
          <DialogContent>
            <Alert severity="success">Report sent. Thank you.</Alert>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={closeNow} variant="contained" autoFocus sx={{ textTransform: 'none' }}>
              Close
            </Button>
          </DialogActions>
        </>
      ) : (
        <form onSubmit={handleSubmit} noValidate>
          <DialogContent sx={{ pt: 1 }}>
            {errorMessage && (
              <Alert severity="error" sx={{ mb: 1.5 }}>
                {errorMessage}
              </Alert>
            )}
            <TextField
              label="Title"
              value={fields.title}
              onChange={setField('title')}
              onBlur={markTouched('title')}
              error={Boolean(fieldError('title'))}
              helperText={fieldError('title') || `${L.titleMin}-${L.titleMax} characters`}
              required
              autoFocus
              fullWidth
              margin="dense"
              size="small"
              disabled={submitting}
            />
            <TextField
              label="What happened"
              value={fields.description}
              onChange={setField('description')}
              onBlur={markTouched('description')}
              error={Boolean(fieldError('description'))}
              helperText={
                fieldError('description')
                || `${L.descriptionMin}-${L.descriptionMax} characters (${countChars(fields.description)}/${L.descriptionMax})`
              }
              required
              minRows={3}
              maxRows={8}
              disabled={submitting}
              {...multiline}
            />
            <TextField
              label="Steps to reproduce (optional)"
              value={fields.steps}
              onChange={setField('steps')}
              onBlur={markTouched('steps')}
              error={Boolean(fieldError('steps'))}
              helperText={fieldError('steps') || `Up to ${L.stepsMax} characters`}
              minRows={2}
              maxRows={6}
              disabled={submitting}
              {...multiline}
            />
            <TextField
              label="Contact email (optional)"
              type="email"
              value={fields.email}
              onChange={setField('email')}
              onBlur={markTouched('email')}
              error={Boolean(fieldError('email'))}
              helperText={fieldError('email') || 'Only used to follow up on this report'}
              fullWidth
              margin="dense"
              size="small"
              autoComplete="email"
              disabled={submitting}
            />
            <Box sx={{ mt: 1.5 }} data-testid="bug-report-context">
              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                Attached automatically
              </Typography>
              <Typography
                variant="caption"
                sx={{ display: 'block', color: 'text.secondary', wordBreak: 'break-all' }}
              >
                Page: {context.pageUrl || 'unavailable'}
              </Typography>
              <Typography
                variant="caption"
                sx={{ display: 'block', color: 'text.secondary', wordBreak: 'break-word' }}
              >
                Browser: {context.userAgent || 'unavailable'}
              </Typography>
            </Box>
          </DialogContent>

          {confirmDiscard ? (
            <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Alert severity="warning" sx={{ flex: '1 1 100%' }}>
                Discard report?
              </Alert>
              <Button
                onClick={() => setConfirmDiscard(false)}
                autoFocus
                sx={{ textTransform: 'none' }}
              >
                Keep editing
              </Button>
              <Button onClick={closeNow} color="error" sx={{ textTransform: 'none' }}>
                Discard
              </Button>
            </DialogActions>
          ) : (
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={requestClose} disabled={sending} sx={{ textTransform: 'none' }}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={submitting}
                sx={{ textTransform: 'none' }}
              >
                {submitting ? 'Sending...' : 'Send report'}
              </Button>
            </DialogActions>
          )}
        </form>
      )}
    </Dialog>
  );
}
